import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import api from "@/lib/api";

const HERO_IMG = "/photos/prieure-accueil.jpg";

const COPY = {
  fr: {
    kicker: "Rejoignez l'aventure",
    title: "Devenir bénévole",
    lead:
      "Les Fêtes de saint Léonard — Millenium 2027 ne pourront vivre sans vous. Logistique, accueil, restauration, communication, soutien artistique… nombreuses sont les missions qui vous attendent au sein d'une équipe joyeuse et engagée.",
    formTitle: "Votre candidature",
    formIntro:
      "Remplissez ce formulaire et nous vous recontacterons rapidement. Vous recevrez un email d'activation pour créer votre Espace Membre.",
    name: "Nom complet *",
    email: "Email *",
    phone: "Téléphone",
    city: "Ville",
    country: "Pays",
    interests: "Centres d'intérêt (cochez plusieurs si vous le souhaitez)",
    motivation: "Votre motivation, vos disponibilités, vos compétences",
    motivationPlaceholder:
      "Quelques lignes pour vous présenter — pas de discours formel, parlez-nous simplement de vous.",
    submit: "Envoyer ma candidature",
    sending: "Envoi en cours…",
    successTitle: "Merci pour votre engagement !",
    successText:
      "Votre candidature est bien enregistrée. Vous allez recevoir d'ici quelques minutes un email d'activation pour créer votre Espace Membre. Un membre de l'équipe vous recontactera très prochainement.",
    backHome: "Retour à l'accueil",
    member: "Déjà bénévole ?",
    memberLink: "Connectez-vous à votre Espace",
    nameError: "Veuillez indiquer votre nom complet.",
    emailError: "Email invalide.",
    interestsError: "Choisissez au moins un centre d'intérêt.",
    consent:
      "En envoyant ce formulaire, vous acceptez que vos données soient conservées pour la gestion de votre candidature et la coordination des Fêtes. Vous pouvez demander leur suppression à tout moment.",
  },
  en: {
    kicker: "Join the journey",
    title: "Become a volunteer",
    lead:
      "The Saint Léonard Festivities — Millenium 2027 cannot happen without you. Logistics, hospitality, food service, communication, artistic support… many missions await within a joyful and committed team.",
    formTitle: "Your application",
    formIntro:
      "Fill in this form and we will get back to you shortly. You will receive an activation email to create your Member Area.",
    name: "Full name *",
    email: "Email *",
    phone: "Phone",
    city: "City",
    country: "Country",
    interests: "Areas of interest (tick as many as you like)",
    motivation: "Your motivation, availability, skills",
    motivationPlaceholder:
      "A few lines to introduce yourself — no formal speech, just tell us who you are.",
    submit: "Send my application",
    sending: "Sending…",
    successTitle: "Thank you for your commitment!",
    successText:
      "Your application has been received. You will receive an activation email shortly to create your Member Area. A team member will be in touch very soon.",
    backHome: "Back to home",
    member: "Already a volunteer?",
    memberLink: "Sign in to your space",
    nameError: "Please enter your full name.",
    emailError: "Invalid email.",
    interestsError: "Please pick at least one area of interest.",
    consent:
      "By sending this form, you agree that your data may be stored to handle your application and coordinate the Festivities. You may request deletion at any time.",
  },
};

const INTEREST_LABELS_EN = {
  accueil: "Welcome & guidance",
  logistique: "Logistics & setup",
  restauration: "Catering",
  communication: "Communication & media",
  billetterie: "Ticketing & registration",
  securite: "Safety & first aid",
  transport: "Transport & shuttles",
  artistique: "Artistic support (stage, sound…)",
  autre: "Other / let's discuss",
};

export default function Volunteer() {
  const { locale } = useI18n();
  const t = COPY[locale === "en" ? "en" : "fr"];
  const [interestsList, setInterestsList] = useState({ keys: [], labels_fr: {} });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", country: "",
    motivation: "", interests: [],
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get("/volunteer/interests").then((r) => {
      setInterestsList({ keys: r.data.interests || [], labels_fr: r.data.labels_fr || {} });
    }).catch(() => {});
  }, []);

  const on = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const toggleInterest = (key) => {
    setForm((s) => ({
      ...s,
      interests: s.interests.includes(key)
        ? s.interests.filter((i) => i !== key)
        : [...s.interests, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError(t.nameError); return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setError(t.emailError); return;
    }
    if (form.interests.length === 0) {
      setError(t.interestsError); return;
    }
    setBusy(true);
    try {
      await api.post("/volunteer/apply", { ...form, locale });
      setSuccess(true);
    } catch (ex) {
      setError(ex?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div data-testid="volunteer-success">
        <section className="relative" style={{ height: "42vh", minHeight: 320 }}>
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-vignette" />
          <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
            <div className="font-caps text-[0.72rem] text-white/90 mb-4">{t.kicker}</div>
            <h1 className="font-display text-5xl sm:text-7xl text-white">{t.successTitle}</h1>
          </div>
        </section>
        <section className="py-24">
          <div className="container-prieure max-w-2xl text-center">
            <Ornament className="mb-8" />
            <p className="font-display italic text-2xl mb-8" style={{ color: "var(--ink-2)" }}>
              {t.successText}
            </p>
            <Link to="/" className="btn btn-primary">{t.backHome}</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="volunteer-page">
      <section className="relative" style={{ height: "48vh", minHeight: 360 }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <div className="font-caps text-[0.72rem] text-white/90 mb-4">{t.kicker}</div>
          <h1 className="font-display text-5xl sm:text-7xl text-white">{t.title}</h1>
        </div>
      </section>

      <section className="py-20">
        <div className="container-prieure max-w-3xl">
          <Ornament className="mb-8" />
          <p className="font-display italic text-2xl text-center leading-relaxed" style={{ color: "var(--ink-2)" }}>
            {t.lead}
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="container-prieure max-w-2xl">
          <div className="deco-rule mb-6">
            <span className="font-caps text-[0.78rem]">{t.formTitle}</span>
          </div>
          <p className="font-body text-sm mb-8" style={{ color: "var(--muted)" }}>
            {t.formIntro}
          </p>

          <form onSubmit={submit} className="bg-white border p-8 grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ borderColor: "var(--border)" }}>
            <label className="block sm:col-span-2">
              <span className="label-caps">{t.name}</span>
              <input data-testid="vol-name" value={form.name} onChange={on("name")} required className="input-classic mt-1" />
            </label>
            <label className="block">
              <span className="label-caps">{t.email}</span>
              <input data-testid="vol-email" type="email" value={form.email} onChange={on("email")} required className="input-classic mt-1" />
            </label>
            <label className="block">
              <span className="label-caps">{t.phone}</span>
              <input data-testid="vol-phone" value={form.phone} onChange={on("phone")} className="input-classic mt-1" />
            </label>
            <label className="block">
              <span className="label-caps">{t.city}</span>
              <input data-testid="vol-city" value={form.city} onChange={on("city")} className="input-classic mt-1" />
            </label>
            <label className="block">
              <span className="label-caps">{t.country}</span>
              <input data-testid="vol-country" value={form.country} onChange={on("country")} className="input-classic mt-1" />
            </label>

            <div className="block sm:col-span-2">
              <span className="label-caps">{t.interests}</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                {interestsList.keys.map((key) => {
                  const label = locale === "en" ? (INTEREST_LABELS_EN[key] || key) : (interestsList.labels_fr[key] || key);
                  const active = form.interests.includes(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleInterest(key)}
                      data-testid={`vol-interest-${key}`}
                      className="text-left px-4 py-2 border transition-colors font-body text-sm"
                      style={{
                        borderColor: active ? "var(--cinnabar)" : "var(--border)",
                        background: active ? "var(--parchment-2)" : "white",
                        color: active ? "var(--cinnabar)" : "var(--ink)",
                      }}
                    >
                      <span className="inline-block w-4">{active ? "✓" : ""}</span>{label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="block sm:col-span-2">
              <span className="label-caps">{t.motivation}</span>
              <textarea
                data-testid="vol-motivation"
                rows={5}
                value={form.motivation}
                onChange={on("motivation")}
                placeholder={t.motivationPlaceholder}
                className="input-classic mt-1"
              />
            </label>

            {error && (
              <p className="sm:col-span-2 font-body text-sm" style={{ color: "var(--cinnabar)" }} data-testid="vol-error">
                {error}
              </p>
            )}

            <p className="sm:col-span-2 font-body text-xs italic" style={{ color: "var(--muted)" }}>
              {t.consent}
            </p>

            <div className="sm:col-span-2 flex flex-wrap items-center gap-4 mt-2">
              <button type="submit" disabled={busy} data-testid="vol-submit" className="btn btn-primary">
                {busy ? t.sending : t.submit}
              </button>
              <span className="font-body text-sm" style={{ color: "var(--muted)" }}>
                {t.member}{" "}
                <Link to="/espace/connexion" className="link-underline" style={{ color: "var(--cinnabar)" }}>
                  {t.memberLink}
                </Link>
              </span>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
